import type {
  QuestionnaireItemAnswerOptionWithTranslations,
  QuestionnaireItemWithTranslations,
  QuestionnaireWithTranslations,
} from '@liza/fhir-utils';
import {
  buildScoreObservationTemplate,
  buildTemplateExtractExtension,
  frequencyAnswerOption,
  type ItemFraming,
  SEMVER_VERSION_ALGORITHM_EXTENSION,
  symptomItem,
  withEs,
} from './scoredSurvey';

/**
 * GAD-7 (Generalized Anxiety Disorder 7-item) anxiety screening instrument. Item structure and
 * LOINC coding follow the HL7 SDC GAD-7 panel (LOINC 69737-5), published here under our own
 * canonical `url` and version, mirroring {@link ./phq9} — the two share the scored-survey
 * machinery in {@link ./scoredSurvey}.
 *
 * Q1–Q7 are required and scored on the standard 0–3 frequency scale ({@link frequencyAnswerOptions});
 * Q8 is an unscored, optional functional-difficulty item on a separate 4-option difficulty scale.
 * The total score (0–21) is derived server-side via Medplum's SDC `QuestionnaireResponse/$extract`:
 * a contained Observation template carrying the total-score LOINC plus standard
 * `sdc-questionnaire-templateExtract` annotations. Behavior is verified by
 * `src/__tests__/liveMedplum/gad7ExtractScoring.test.ts`.
 *
 * Source: the GAD-7 FHIR survey JSON attached to the originating issue (English + Spanish), whose
 * item structure and LOINC coding match the HL7 Patient-Centered Outcomes IG GAD-7 panel
 * (http://hl7.org/fhir/us/pco/STU1/en/Questionnaire-69737-5.html). The
 * English item text is the standard validated GAD-7 wording, with the "over the last 2 weeks"
 * timeframe carried in a per-item framing extension on Q1–Q7 (so Q8, a different format, stands
 * alone) — the same per-item framing pattern PHQ-9 uses. The inline Spanish translations on the
 * `_`-sibling elements are transcribed from the Spanish source JSON. Because GAD-7 is a validated
 * instrument, these strings must be reviewed against the official bilingual GAD-7 before go-live
 * and never free-translated; scoring validity depends on the exact wording. GAD-7 was developed by
 * Drs. Robert L. Spitzer, Janet B.W. Williams, Kurt Kroenke and colleagues, with an educational
 * grant from Pfizer Inc. No permission required to reproduce, translate, display or distribute.
 */

/** GAD-7 total score. Distinct from the panel LOINC (69737-5) on `Questionnaire.code`. */
export const GAD7_TOTAL_SCORE_LOINC = '70274-6';

/**
 * Bilingual "Over the last 2 weeks…" framing for Q1–Q7, passed to {@link symptomItem}. Q8
 * (functional difficulty, different format) omits framing by passing none.
 */
const gad7Framing: ItemFraming = {
  en: 'Over the last 2 weeks, how often have you been bothered by the following problems?',
  es: 'Durante las últimas 2 semanas, ¿con qué frecuencia le han molestado los siguientes problemas?',
};

const frequencyAnswerOptions: QuestionnaireItemAnswerOptionWithTranslations[] = [
  frequencyAnswerOption('0', 0, 'LA6568-5', 'Not at all', 'Nunca'),
  frequencyAnswerOption('1', 1, 'LA6569-3', 'Several days', 'Varios días'),
  frequencyAnswerOption('2', 2, 'LA6570-1', 'More than half the days', 'Más de la mitad de los días'),
  frequencyAnswerOption('3', 3, 'LA6571-9', 'Nearly every day', 'Casi todos los días'),
];

const scoredSymptomItems: QuestionnaireItemWithTranslations[] = [
  symptomItem(
    '/69725-0',
    '69725-0',
    'Feeling nervous, anxious, or on edge',
    'Feeling nervous, anxious, or on edge',
    'Sentirse nervioso/a, intranquilo/a o con los nervios de punta',
    true,
    frequencyAnswerOptions,
    gad7Framing,
  ),
  symptomItem(
    '/68509-9',
    '68509-9',
    'Not being able to stop or control worrying',
    'Not being able to stop or control worrying',
    'No poder dejar de preocuparse o no poder controlar la preocupación',
    true,
    frequencyAnswerOptions,
    gad7Framing,
  ),
  symptomItem(
    '/69733-4',
    '69733-4',
    'Worrying too much about different things',
    'Worrying too much about different things',
    'Preocuparse demasiado por diferentes cosas',
    true,
    frequencyAnswerOptions,
    gad7Framing,
  ),
  symptomItem(
    '/69734-2',
    '69734-2',
    'Trouble relaxing',
    'Trouble relaxing',
    'Dificultad para relajarse',
    true,
    frequencyAnswerOptions,
    gad7Framing,
  ),
  symptomItem(
    '/69735-9',
    '69735-9',
    'Being so restless that it is hard to sit still',
    'Being so restless that it is hard to sit still',
    'Estar tan inquieto/a que es difícil permanecer sentado/a tranquilamente',
    true,
    frequencyAnswerOptions,
    gad7Framing,
  ),
  symptomItem(
    '/69689-8',
    '69689-8',
    'Becoming easily annoyed or irritable',
    'Becoming easily annoyed or irritable',
    'Molestarse o ponerse irritable fácilmente',
    true,
    frequencyAnswerOptions,
    gad7Framing,
  ),
  symptomItem(
    '/69736-7',
    '69736-7',
    'Feeling afraid as if something awful might happen',
    'Feeling afraid as if something awful might happen',
    'Sentir miedo como si algo terrible pudiera pasar',
    true,
    frequencyAnswerOptions,
    gad7Framing,
  ),
];

/**
 * Q8 — functional difficulty question. Not scored (all ordinalValues = 0); not in scoredSymptomItems.
 * No framing extension → no "Over the last 2 weeks…" prompt shown above it.
 * LOINC: 69722-7.
 *
 * Q8 shares LOINC 69722-7 with PHQ-9's Q10, but the Spanish wording intentionally differs (e.g.
 * 'No es nada difícil' here vs. PHQ-9's 'Para nada difícil'): each is transcribed verbatim from its
 * own validated bilingual source survey, and those sources don't translate the shared question
 * identically. Do not reconcile them — scoring/clinical validity depends on matching each
 * instrument's source.
 */
const q8FunctionalDifficulty: QuestionnaireItemWithTranslations = symptomItem(
  '/69722-7',
  '69722-7',
  'If you checked off any problems, how difficult have these made it for you to do your work, take care of things at home, or get along with other people?',
  'If you checked off any problems, how difficult have these made it for you to do your work, take care of things at home, or get along with other people?',
  'Si marcó cualquier problema, ¿qué tan difícil le han hecho estos problemas para hacer su trabajo, ocuparse de las cosas en casa o llevarse bien con otras personas?',
  false,
  [
    frequencyAnswerOption('0', 0, 'LA6572-7', 'Not difficult at all', 'No es nada difícil'),
    frequencyAnswerOption('0', 0, 'LA6573-5', 'Somewhat difficult', 'Algo difícil'),
    frequencyAnswerOption('0', 0, 'LA6575-0', 'Very difficult', 'Muy difícil'),
    frequencyAnswerOption('0', 0, 'LA6574-3', 'Extremely difficult', 'Extremadamente difícil'),
  ],
);

const gad7ScoreObservationTemplate = buildScoreObservationTemplate({
  id: 'gad7-score-tmpl',
  totalScoreCode: GAD7_TOTAL_SCORE_LOINC,
  display: 'GAD-7',
  scoredItems: scoredSymptomItems,
});

export const gad7Questionnaire: QuestionnaireWithTranslations = {
  resourceType: 'Questionnaire',
  meta: {
    profile: ['http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaire'],
  },
  extension: [SEMVER_VERSION_ALGORITHM_EXTENSION, buildTemplateExtractExtension('gad7-score-tmpl')],
  contained: [gad7ScoreObservationTemplate],
  url: 'https://liza.health/fhir/questionnaire/gad-7',
  identifier: [{ system: 'urn:ietf:rfc:3986', value: 'https://liza.health/fhir/questionnaire/gad-7' }],
  version: '1',
  name: 'GAD7',
  language: 'en',
  title: 'GAD-7',
  _title: withEs('Trastorno de ansiedad generalizada (GAD-7)'),
  status: 'active',
  subjectType: ['Patient'],
  date: '2026-06-22',
  publisher: 'Liza Health',
  jurisdiction: [{ coding: [{ system: 'urn:iso:std:iso:3166', code: 'US' }] }],
  copyright:
    'This material contains content from LOINC® (http://loinc.org). LOINC is copyright © 1995-2024, Regenstrief Institute, Inc. and the LOINC Committee, and is available at no cost under the license at http://loinc.org/license. Copyright © Pfizer Inc. All rights reserved. Developed by Drs. Robert L. Spitzer, Janet B.W. Williams, Kurt Kroenke and colleagues, with an educational grant from Pfizer Inc. No permission required to reproduce, translate, display or distribute (https://www.phqscreeners.com/).',
  code: [{ system: 'http://loinc.org', code: '69737-5', display: 'Generalized anxiety disorder 7 item (GAD-7)' }],
  item: [...scoredSymptomItems, q8FunctionalDifficulty],
};
